import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  MobileStepper,
} from '@mui/material';
import Apple from '@mui/icons-material/Apple';
import Android from '@mui/icons-material/Android';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import QrCode from '@mui/icons-material/QrCode';
import { QRCodeSVG } from 'qrcode.react';
import { downloadWalletPass } from '../../utils/walletPass';

interface Ticket {
  id: string;
  ticket_number: string;
  qr_code_data: string;
  event_name: string;
  event_date: string;
  ticket_type: string;
}

interface WalletPassDialogProps {
  open: boolean;
  onClose: () => void;
  passData: {
    tickets: Ticket[];
    purchaseId: string;
  } | null;
}

export const WalletPassDialog: React.FC<WalletPassDialogProps> = ({
  open,
  onClose,
  passData,
}) => {
  const [activeTicketIndex, setActiveTicketIndex] = useState(0);

  if (!passData || !passData.tickets || passData.tickets.length === 0) {
    return null;
  }

  const currentTicket = passData.tickets[activeTicketIndex];
  const maxSteps = passData.tickets.length;

  const handleNext = () => {
    setActiveTicketIndex((prev) => Math.min(prev + 1, maxSteps - 1));
  };

  const handleBack = () => {
    setActiveTicketIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleAddToWallet = (type: 'apple' | 'google') => {
    const walletData = {
      qrCodeData: currentTicket.qr_code_data,
      ticketNumber: currentTicket.ticket_number,
      eventName: currentTicket.event_name,
      eventDate: currentTicket.event_date,
      ticketType: currentTicket.ticket_type,
      quantity: 1,
    };
    downloadWalletPass(walletData, type);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { 'data-tour': 'wallet-pass-dialog' } as React.HTMLAttributes<HTMLDivElement> }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCode color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Your Event Ticket{maxSteps > 1 ? 's' : ''}
            </Typography>
          </Box>
          {maxSteps > 1 && (
            <Typography variant="body2" color="text.secondary">
              {activeTicketIndex + 1} of {maxSteps}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Scan this code at the event entrance
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 2,
              p: 2,
              bgcolor: 'white',
              borderRadius: 2,
            }}
          >
            <QRCodeSVG value={currentTicket.qr_code_data} size={200} level="H" />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Ticket #{currentTicket.ticket_number}
          </Typography>
        </Paper>

        {maxSteps > 1 && (
          <MobileStepper
            steps={maxSteps}
            position="static"
            activeStep={activeTicketIndex}
            sx={{ mb: 3, bgcolor: 'transparent', justifyContent: 'center' }}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
                disabled={activeTicketIndex === maxSteps - 1}
              >
                Next
                <KeyboardArrowRight />
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handleBack}
                disabled={activeTicketIndex === 0}
              >
                <KeyboardArrowLeft />
                Back
              </Button>
            }
          />
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Add to Wallet
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Apple />}
              onClick={() => handleAddToWallet('apple')}
              sx={{
                py: 1.5,
                borderColor: 'text.primary',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'text.primary',
                  bgcolor: 'grey.100',
                },
              }}
            >
              Add to Apple Wallet
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Android />}
              onClick={() => handleAddToWallet('google')}
              sx={{
                py: 1.5,
                borderColor: 'text.primary',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'text.primary',
                  bgcolor: 'grey.100',
                },
              }}
            >
              Add to Google Pay
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Event Details
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Event
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {currentTicket.event_name}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {currentTicket.event_date}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Ticket Type
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                {currentTicket.ticket_type}
              </Typography>
            </Box>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            You can also access your tickets anytime from your purchases page or email confirmation
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="contained" fullWidth>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
